/**
 * Authentication requests and the queries around them.
 *
 * The only place that knows the auth endpoints exist.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type {
  CurrentUser,
  InvitationPreview,
  LoginRequest,
  SetPasswordRequest,
} from '@/api/schema';

import { markSessionEnded } from './sessionEnded';

/** One key for "who is signed in", so nothing else has to guess it. */
export const CURRENT_USER_KEY = ['auth', 'me'] as const;

export function useCurrentUser() {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: CURRENT_USER_KEY,
    queryFn: async () => {
      try {
        return await api.get<CurrentUser>('/api/v1/auth/me');
      } catch (error) {
        // Not signed in is an answer, not a failure. Returning null keeps the guard
        // simple and stops the query being retried or reported as an error.
        if (error instanceof ApiError && error.isUnauthenticated) {
          // Recorded here as well as in providers.tsx, and it has to be. Because
          // this query resolves rather than throwing, its 401 never reaches the
          // QueryCache error handler — yet this is the query most likely to meet
          // an expiry first: staleTime is 0 and window focus refetches it, which
          // is exactly the neglected-tab case the banner exists for. Without
          // this line that user gets a bare sign-in form and no explanation.
          //
          // Only when somebody was signed in. Undefined on a first visit, null
          // once we know there is no session; both correctly decline to mark.
          if (queryClient.getQueryData(CURRENT_USER_KEY)) markSessionEnded();
          return null;
        }
        throw error;
      }
    },
    retry: false,
    // Checked on every mount: a session can expire while a tab sits open.
    staleTime: 0,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: LoginRequest) => api.post<CurrentUser>('/api/v1/auth/login', body),
    onSuccess: (user) => {
      // Seed the cache from the response rather than refetching: the login response
      // is the same payload /auth/me would return.
      queryClient.setQueryData(CURRENT_USER_KEY, user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.post('/api/v1/auth/logout');
    },
    onSettled: () => {
      // Cleared whether or not the request succeeded: the user asked to be signed
      // out, and leaving another person's data in the cache would be worse than a
      // stale session cookie.
      queryClient.clear();
      queryClient.setQueryData(CURRENT_USER_KEY, null);
    },
  });
}

/** Validates an invitation or reset link before the set-password form renders. */
export function useInvitation(token: string) {
  return useQuery({
    queryKey: ['auth', 'invitation', token],
    queryFn: () => api.get<InvitationPreview>('/api/v1/auth/invitation', { query: { token } }),
    retry: false,
    enabled: token.length > 0,
  });
}

export function useSetPassword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: SetPasswordRequest) =>
      api.post<CurrentUser>('/api/v1/auth/accept-invite', body),
    onSuccess: (user) => {
      // The API signs them in, so nobody has to retype what they just chose.
      queryClient.setQueryData(CURRENT_USER_KEY, user);
    },
  });
}
